import { useInfiniteQuery } from "@tanstack/react-query";

import {
  listUnitSessions,
  type UnitSessionsResponse,
} from "@/api/fleet";
import { formatDate } from "@/lib/utils";

interface Props {
  unitId: number;
}

export function SessionsTab({ unitId }: Props) {
  const q = useInfiniteQuery<UnitSessionsResponse>({
    queryKey: ["unit-sessions", unitId],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      listUnitSessions(unitId, {
        limit: 50,
        cursor: pageParam as number | undefined,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const rows =
    q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <section className="space-y-3" data-testid="unit-sessions">
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">Sesiones</h2>
        <p className="text-xs text-muted-200">
          {rows.length} resultado(s){q.hasNextPage ? " · más disponibles" : ""}
        </p>
      </header>

      {q.isLoading && <div className="text-muted-200">Cargando…</div>}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-xs uppercase text-muted-200 border-b border-border">
              <th className="px-2 py-2 text-left">Inicio</th>
              <th className="px-2 py-2 text-left">Fin</th>
              <th className="px-2 py-2 text-left">Duración</th>
              <th className="px-2 py-2 text-left">Motivo</th>
              <th className="px-2 py-2 text-left">Operador</th>
              <th className="px-2 py-2 text-left">Comentarios</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr
                key={s.sesionId}
                className="border-b border-border/40 hover:bg-surface2"
              >
                <td className="px-2 py-1.5">{formatDate(s.inicio)}</td>
                <td className="px-2 py-1.5">
                  {s.fin ? formatDate(s.fin) : "—"}
                </td>
                <td className="px-2 py-1.5">
                  {s.duracionMinutos != null
                    ? formatMinutes(s.duracionMinutos)
                    : "—"}
                </td>
                <td className="px-2 py-1.5">
                  {s.motivoNombre ?? `#${s.motivoId}`}
                </td>
                <td className="px-2 py-1.5">{s.operadorNombre ?? "—"}</td>
                <td className="px-2 py-1.5 text-muted-200 truncate max-w-xs">
                  {s.comentarios ?? "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !q.isLoading && (
              <tr>
                <td className="px-2 py-4 text-center text-muted-200" colSpan={6}>
                  Sin sesiones en el rango.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {q.hasNextPage && (
        <div className="text-center">
          <button
            className="btn-ghost text-xs"
            onClick={() => q.fetchNextPage()}
            disabled={q.isFetchingNextPage}
          >
            {q.isFetchingNextPage ? "Cargando…" : "Cargar más"}
          </button>
        </div>
      )}
    </section>
  );
}

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
